import { useRef, useState, type ChangeEvent } from 'react';
import { ApiError } from '../../lib/api';
import { pushToast } from '../../lib/events';
import type { ImageValue } from '../../lib/fieldValue';
import { describeUploadError, imageUrl, uploadImage } from '../../lib/whiteboard';

interface ImageFieldProps {
  roomId: number;
  value: ImageValue | null;
  onCommit: (value: ImageValue | null) => void;
  disabled?: boolean;
}

function randomFieldFileId(): string {
  const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  return `field-${uuid}`;
}

/**
 * The WP7 image-cell editor: an upload button (drag-and-drop isn't offered here — Excalidraw's own
 * canvas is the paste/drop surface, per DESIGN.md; this cell is a plain upload) that stores
 * `ImageValue{imageAssetId, alt}`, plus a thumbnail and an optional alt-text field once one is set.
 * `fileId` is always `"field-" + uuid` (contract: `WP7-whiteboard.md`), never an Excalidraw file id
 * — this cell has nothing to do with the whiteboard's own file registry.
 */
export function ImageField({ roomId, value, onCommit, disabled = false }: ImageFieldProps) {
  const [alt, setAlt] = useState(value?.alt ?? '');
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset the input so selecting the exact same file again still fires a change event.
    event.target.value = '';
    if (!file || disabled) {
      return;
    }
    setUploading(true);
    try {
      const asset = await uploadImage(roomId, file, randomFieldFileId());
      const trimmedAlt = alt.trim();
      onCommit({ type: 'IMAGE', imageAssetId: asset.id, alt: trimmedAlt === '' ? null : trimmedAlt });
    } catch (error) {
      pushToast(error instanceof ApiError ? describeUploadError(error) : 'Could not upload that image.');
    } finally {
      setUploading(false);
    }
  }

  function commitAlt() {
    if (disabled || !value) {
      return;
    }
    const trimmedAlt = alt.trim();
    onCommit({ ...value, alt: trimmedAlt === '' ? null : trimmedAlt });
  }

  function removeImage() {
    if (disabled) {
      return;
    }
    setAlt('');
    onCommit(null);
  }

  return (
    <div className="flex flex-col gap-3">
      {value ? (
        <div className="flex items-start gap-3">
          <img
            src={imageUrl(roomId, value.imageAssetId)}
            alt={value.alt ?? ''}
            className="h-24 w-24 shrink-0 rounded-md border border-border-strong object-cover"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <label className="flex flex-col gap-1 text-sm text-text-muted">
              <span className="eyebrow">Alt text (optional)</span>
              <input
                type="text"
                value={alt}
                disabled={disabled}
                onChange={(event) => setAlt(event.target.value)}
                onBlur={commitAlt}
                className="rounded-md border border-border-strong bg-bg px-3 py-2 text-text focus:border-accent"
              />
            </label>
            {!disabled && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                  className="min-h-tap rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted transition-colors hover:border-accent-border hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {uploading ? 'Uploading…' : 'Replace'}
                </button>
                <button
                  type="button"
                  onClick={removeImage}
                  disabled={uploading}
                  className="min-h-tap rounded-md border border-border-strong px-3 text-sm font-semibold text-signal transition-colors hover:border-signal disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      ) : disabled ? (
        <p className="text-sm text-text-faint">No image uploaded.</p>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="rounded-md border border-dashed border-border-strong px-4 py-6 text-center transition-colors hover:border-accent-border disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="eyebrow block">{uploading ? 'Uploading…' : 'Upload image'}</span>
          <span className="mt-2 block text-sm text-text-faint">PNG, JPEG, GIF, or WEBP — up to 10 MB.</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="sr-only"
        aria-label="Upload image"
        disabled={disabled}
        onChange={(event) => void handleFileChange(event)}
      />
    </div>
  );
}
